/**
 * Adresse canonique (et og:url) dans la LANGUE de la page.
 *
 * Les pages déclarent leur canonique en dur, sur l'adresse française
 * (`https://donoor.org/faq`). Sur /en/faq, /de/faq… cette balise disait à
 * Google « la vraie page est la française » : les versions traduites
 * n'étaient jamais indexées, malgré les hreflang (plugins/hreflang.ts).
 *
 * Plutôt que de corriger ~90 pages une à une (et d'oublier les prochaines),
 * on réécrit au moment où l'en-tête est assemblé : hors français, une adresse
 * du site sans préfixe de langue reçoit celui de la page (/en, /de…). Les
 * adresses externes et celles déjà préfixées ne bougent pas. Universel
 * (SSR + client) : la balise doit être juste dans le HTML servi aux robots.
 */
import { LANGUES } from "~/utils/langue";

export default defineNuxtPlugin((nuxtApp) => {
  const head = injectHead();
  const siteUrl = ((useSiteConfig().url as string) || "https://donoor.org").replace(/\/$/, "");

  const localiser = (url: unknown, langue: string): unknown => {
    if (typeof url !== "string" || !url.startsWith(siteUrl)) return url;
    const chemin = url.slice(siteUrl.length) || "/";
    if (!chemin.startsWith("/")) return url; // autre domaine (donoor.org.evil…)
    const premier = chemin.split(/[/?#]/)[1] ?? "";
    if ((LANGUES as readonly string[]).includes(premier)) return url; // déjà préfixée
    return `${siteUrl}/${langue}${chemin === "/" ? "" : chemin}`;
  };

  head.hooks?.hook("tags:resolve", (ctx) => {
    const langue = nuxtApp.$i18n.locale.value;
    if (langue === "fr") return;
    for (const balise of ctx.tags) {
      const p = balise.props as Record<string, unknown>;
      if (balise.tag === "link" && p.rel === "canonical") p.href = localiser(p.href, langue);
      else if (balise.tag === "meta" && p.property === "og:url") p.content = localiser(p.content, langue);
    }
  });
});
